import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { GenerationResult, ScoredAd, ForeplayAd } from '@/types'

interface ResultDetailModalProps {
  result: GenerationResult
  referenceAd: ScoredAd | ForeplayAd | null
  currentIdx: number
  total: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

// Helper to get properties from either ScoredAd or ForeplayAd
function getAdInfo(ad: ScoredAd | ForeplayAd | null) {
  if (!ad) return null

  // Check if it's a ScoredAd (has 'ad' property)
  if ('ad' in ad) {
    return {
      url: ad.ad.url || ad.ad.download_s3_url || '',
      brandName: ad.ad.brand_name,
      score: ad.overall_score,
    }
  }

  // It's a ForeplayAd
  return {
    url: ad.url || ad.download_s3_url || '',
    brandName: ad.brand_name,
    score: ad.overall_score,
  }
}

export function ResultDetailModal({
  result,
  referenceAd,
  currentIdx,
  total,
  onClose,
  onPrev,
  onNext,
}: ResultDetailModalProps) {
  const adInfo = getAdInfo(referenceAd)

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        {/* Navigation arrows + main image */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onPrev}
            disabled={currentIdx === 0}
            className="flex-shrink-0"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <div className="flex-1 flex justify-center">
            <img
              src={result.url || ''}
              alt=""
              className="max-h-[60vh] max-w-full object-contain rounded-lg"
            />
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={onNext}
            disabled={currentIdx === total - 1}
            className="flex-shrink-0"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>

        {/* Reference image info */}
        {adInfo && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-500 mb-2">Reference Used:</p>
            <div className="flex items-center gap-3">
              <img
                src={adInfo.url}
                alt=""
                className="w-20 h-20 object-cover rounded border"
              />
              <div className="text-sm">
                {adInfo.brandName && (
                  <p className="font-medium">{adInfo.brandName}</p>
                )}
                {adInfo.score != null && (
                  <p className="text-gray-500">Score: {Math.round(adInfo.score)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Counter */}
        <p className="text-center text-sm text-gray-400 mt-2">
          {currentIdx + 1} / {total}
        </p>
      </DialogContent>
    </Dialog>
  )
}
